import { getActualTreatmentPlanId, type TPatient } from "@/api/models/patient/patient";
import { EPatientDashboardTab } from "@/types/filter/tableFilters";
import TreatmentPlanJobService, {
  ETreatmentPlanJobState,
  ETreatmentPlanJobSystemSignal,
  ETreatmentPlanJobType,
  type ITreatmentPlanJobsFilters,
  type TTreatmentPlanJob,
  type TTreatmentPlanJobMeta,
  type TTreatmentPlanJobSignal,
} from "@/api/models/treatmentPlan/job";
import { computed, type MaybeRef, type MaybeRefOrGetter, type Ref, toRef, watch } from "vue";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

import { ORTHO_FI_URL, TPatientFinancingPlanType } from "@/api/models/patient/financingPlan";
import { TNextVisitMarkType, type TPatientNextVisit } from "@/api/models/patient/nextVisit";
import { type TOrthoNeeds, TOrthoNeedsMarkType } from "@/api/models/patient/orthoNeeds";
import {
  type TOrthoProposedPlan,
  TOrthoProposedPlanMarkType,
} from "@/api/models/patient/orthoProposedPlan";
import { useOrthoProposedPlanMarkMutation } from "@/composables/models/patient/useOrthoPlan";
import { useNextVisitInstructionsMarkMutation } from "@/composables/models/patient/orthoPlan/useNextVisitInstructions";
import { useCreateFinancingPlan } from "@/composables/models/patient/useFinancingPlans";
import { useOrthoNeedsMarkMutation } from "@/composables/models/patient/useOrthoNeeds";
import {
  usePatientCardRefetch,
  useSendDmLoginLinkMutation,
} from "@/composables/models/patient/usePatient";
import { useSnackStore } from "@/stores/snackStore";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { type TJobSource, useTreatmentPlanStore } from "@/stores/treatmentPlanStore";
import type { TAlignerDeliveryInstructions } from "@/api/models/patient/visitInstructions/alignerDelivery";

import type { TNextVisitInstructions } from "@/api/models/patient/visitInstructions/next";
import type { TFinalDebondVisitInstructions } from "@/api/models/patient/visitInstructions/finalDebond";
import { addJobIdToRoute, updateUrlWithJobId } from "@/utils/jobIdUrl";
import { EPatientLinkedEntity } from "@/api/models/patient/linkEntity";
import { useActionReferralAppointment } from "@/composables/models/referral/useReferralAppointment";
import { useArchiveTreatmentPlan } from "@/composables/models/treatmentPlan/useArchiveTreatmentPlan";
import { TReferralAppointmentActions } from "@/api/models/referral/appointment";
import {
  useAcceptReferral,
  usePaginatedReferral,
  useRejectReferral,
} from "@/composables/models/referral/useReferral.ts";
import { EReferralStatus, type TReferral } from "@/api/models/referral/referral";
import { createQueryKeys, useGenericListQuery } from "@/composables/shared/queries";
import { useQueryClient } from "@tanstack/vue-query";
import { useTreatmentPlanJobLabels } from "@/composables/models/treatmentPlan/useTreatmentPlanJobLabels";
import { useAbility } from "@casl/vue";

export enum ETreatmentPlanDialogs {
  SCHEDULE_TC_APPOINTMENT_CREATED = "patient-schedule-tc-appointment-created-dialog",
  SCHEDULE_TC_APPOINTMENT_DONE = "patient-schedule-tc-appointment-done-dialog",
  ORDER_ALIGNERS_ARRIVED = "patient-order-aligners-arrived-dialog",
  ORDER_ALIGNERS_APPLIED = "patient-order-aligners-applied-dialog",
  REFERRAL_CREATED = "patient-referral-created-dialog",
  REFERRAL_PRICE_PAID_PROPOSAL_CREATED = "referral-paid-proposal-created-dialog",
  PATIENT_RECORDS_CREATED = "patient-records-created-dialog",
}

export const TreatmentPlanJobQueryKeys = createQueryKeys(
  TreatmentPlanJobService.queryKey,
  (key) => ({
    availableBackLoopJobs: key<ITreatmentPlanJobsFilters>("backLoop"),
  })
);

// export const useTreatmentPlanJobsOfPatient = (
//   filters: MaybeRefOrGetter<Partial<ITreatmentPlanJobsFilters>>,
//   enabled: MaybeRefOrGetter<boolean> = true
// ) =>
//   useGenericListQuery<TTreatmentPlanJob, Partial<ITreatmentPlanJobsFilters>>({
//     queryKey: TreatmentPlanJobQueryKeys.availableJobs(),
//     params: { filters, enabled },
//     fetcher: (f) =>
//       TreatmentPlanJobService.getAvailableJobs({ treatment_plan: f?.treatment_plan! }, f),
//     permission: $permSubjects.TREATMENT_PLAN__PATIENTTREATMENTJOB,
//     neededKey: ["treatment_plan"],
//   });

export const useTreatmentPlanBackLoopJobsOfPatient = (
  filters: MaybeRefOrGetter<Partial<ITreatmentPlanJobsFilters>>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TTreatmentPlanJob, Partial<ITreatmentPlanJobsFilters>>({
    queryKey: TreatmentPlanJobQueryKeys.availableBackLoopJobs(),
    params: { filters, enabled },
    fetcher: (f) =>
      TreatmentPlanJobService.getAvailableBackLoopJobs({ treatment_plan: f?.treatment_plan! }, f),
    permission: $permSubjects.TREATMENT_PLAN__PATIENTTREATMENTJOB,
    neededKey: ["treatment_plan"],
    suppressErrors: [404],
  });

export const useCreateTreatmentPlanJob = () => {
  return useGenericMutation<TTreatmentPlanJob, Partial<TTreatmentPlanJob>>({
    mutationFn: (data) => TreatmentPlanJobService.create({}, data),
    feature: "treatmentPlanJob.create",
  });
};

export const useUpdateTreatmentPlanJob = () => {
  return useGenericMutation<TTreatmentPlanJob, Partial<TTreatmentPlanJob> & { id: string }>({
    mutationFn: (data) => TreatmentPlanJobService.update({ id: data.reference_id! }, data),
    feature: "treatmentPlanJob.update",
  });
};

export const useDeleteTreatmentPlanJob = () => {
  return useGenericMutation<void, string>({
    mutationFn: (referenceId) => TreatmentPlanJobService.remove({ id: referenceId }),
    feature: "treatmentPlanJob.delete",
  });
};

export const useTreatmentPlanJobReturn = (patient: MaybeRef<TPatient>) => {
  const refPatient = toRef(patient);
  const { refetchPatientCard } = usePatientCardRefetch();
  return useGenericMutation<void, TTreatmentPlanJob>({
    mutationFn: async (returnJob) => {
      await TreatmentPlanJobService.returnJob(refPatient.value, returnJob);
      await refetchPatientCard(refPatient.value.id);
    },
    feature: "treatmentPlanJob.return",
  });
};

interface TJobContext {
  patient: TPatient;
  orthoNeeds?: TOrthoNeeds;
  orthoProposedPlan?: TOrthoProposedPlan;
  alignerDeliveryInstructions?: TAlignerDeliveryInstructions;
  nextVisitInstructions?: TPatientNextVisit;
  nextVisitInstructionsV2?: TNextVisitInstructions;
  finalDebondInstructions?: TFinalDebondVisitInstructions;
  queryClient: ReturnType<typeof useQueryClient>;
  loading: Ref<boolean>;
}

function actionGate(
  ...reasons: Array<{ blocked: boolean; tooltip?: string }>
): Pick<TTreatmentPlanJobMeta, "disabled" | "tooltip"> {
  const active = reasons.find((reason) => reason.blocked);
  return { disabled: !!active, tooltip: active?.tooltip };
}

export const useTreatmentPlanJobActions = (
  jobs: Ref<TTreatmentPlanJob[] | undefined>,
  context: Ref<TJobContext>,
  jobSourceKey: TJobSource = "main"
) => {
  const { t } = useI18n();
  const { can } = useAbility();
  const { resolveText, resolveDialog } = useTreatmentPlanJobLabels();
  const snackStore = useSnackStore();
  const treatmentPlanStore = useTreatmentPlanStore();
  const { refetchPatientCard } = usePatientCardRefetch();
  const markOrthoNeeds = useOrthoNeedsMarkMutation().mutateAsync;
  const markOrthoProposedPlan = useOrthoProposedPlanMarkMutation().mutateAsync;
  const financingPlan = useCreateFinancingPlan().mutateAsync;
  const sendDmLoginLink = useSendDmLoginLinkMutation().mutateAsync;

  const normalizeJobState = (job: TTreatmentPlanJob): TTreatmentPlanJob =>
    jobSourceKey === "backLoop" ? { ...job, state: ETreatmentPlanJobState.PENDING } : job;

  const shouldLoadReferral = computed(
    () =>
      !!context.value.patient?.id &&
      !!jobs.value?.some((job) => {
        const normalizedJob = normalizeJobState(job);
        return (
          normalizedJob.state === ETreatmentPlanJobState.PENDING &&
          (normalizedJob.signal_system_type === ETreatmentPlanJobSystemSignal.REFERRAL_ACCEPTED ||
            normalizedJob.signal_system_type === ETreatmentPlanJobSystemSignal.REFERRAL_REJECTED)
        );
      })
  );

  const acceptReferral = useAcceptReferral().mutateAsync;
  const rejectReferral = useRejectReferral().mutateAsync;

  const referralFilters = computed(() => ({
    patient: context.value.patient.id,
    status: EReferralStatus.NEW,
  }));

  const { data: referralData } = usePaginatedReferral({
    filters: referralFilters,
    enabled: shouldLoadReferral,
  });
  const referrals = computed<TReferral[]>(
    () => referralData.value?.pages.flatMap((page) => page.results) ?? []
  );
  const currentReferral = computed<TReferral | undefined>(() => referrals.value[0]);
  const markNextVisitInstructions = useNextVisitInstructionsMarkMutation().mutateAsync;
  const actionReferralAppointment = useActionReferralAppointment().mutateAsync;
  const archiveTreatmentPlan = useArchiveTreatmentPlan().mutateAsync;
  const router = useRouter();
  const route = useRoute();

  function systemJobsMap(
    job: TTreatmentPlanJob
  ): Partial<
    Record<
      TTreatmentPlanJobSignal,
      Partial<Record<ETreatmentPlanJobState, Partial<TTreatmentPlanJobMeta>>>
    >
  > {
    return {
      [ETreatmentPlanJobSystemSignal.PATIENT_CONFIRMED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/patients/${context.value.patient.id}/edit`,
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.ORTHO_NEEDS_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/ortNeeds/add`,
            query: {
              patient: context.value.patient.id,
            },
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.ORTHO_NEEDS_CONFIRMED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/ortNeeds/${context.value.orthoNeeds ? context.value.orthoNeeds.id : ""}/edit`,
          },
          action: async (): Promise<void> => {
            await markOrthoNeeds(
              {
                type: TOrthoNeedsMarkType.CONFIRM,
                id:
                  context.value.orthoNeeds && context.value.orthoNeeds.id
                    ? context.value.orthoNeeds.id
                    : "",
              },
              {
                onSuccess: () => {
                  snackStore.showSuccess(
                    t("generic.messages.success.confirm", {
                      entity: t("generic.entity.orthoNeedsPlan"),
                    })
                  );
                  router.push(`/rooms/patient/${context.value.patient.id}`);
                },
              }
            );
          },
          disabled: !context.value.orthoNeeds,
          needsConfirmation: true,
        },
      },
      [ETreatmentPlanJobSystemSignal.ORTHO_NEEDS_APPROVED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/ortNeeds/${context.value.orthoNeeds ? context.value.orthoNeeds.id : ""}`,
          },
          action: async (): Promise<void> => {
            await markOrthoNeeds({
              type: TOrthoNeedsMarkType.APPROVE,
              id:
                context.value.orthoNeeds && context.value.orthoNeeds.id
                  ? context.value.orthoNeeds.id
                  : "",
            });
            snackStore.showSuccess(
              t("generic.messages.success.approve", {
                entity: t("generic.entity.orthoNeedsPlan"),
              })
            );
            router.push(`/rooms/patient/${context.value.patient.id}`);
          },
          disabled: !context.value.orthoNeeds,
          needsConfirmation: true,
        },
      },
      [ETreatmentPlanJobSystemSignal.PATIENT_LINKED_TO_ORTHO]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: EPatientLinkedEntity.ORTHO,
        },
      },
      [ETreatmentPlanJobSystemSignal.PATIENT_LINKED_TO_GP]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: EPatientLinkedEntity.GP,
        },
      },
      [ETreatmentPlanJobSystemSignal.PATIENT_LINKED_TO_TC]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: EPatientLinkedEntity.TC_COORDINATOR,
        },
      },
      [ETreatmentPlanJobSystemSignal.PATIENT_LINKED_TO_GP_PRACTICE]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: EPatientLinkedEntity.GP_PRACTICE,
        },
      },
      [ETreatmentPlanJobSystemSignal.PATIENT_LINKED_TO_ORTHO_PRACTICE]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: EPatientLinkedEntity.ORTHO_PRACTICE,
        },
      },
      [ETreatmentPlanJobSystemSignal.SCHEDULE_TC_APPOINTMENT_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: ETreatmentPlanDialogs.SCHEDULE_TC_APPOINTMENT_CREATED,
        },
      },
      [ETreatmentPlanJobSystemSignal.SCHEDULE_TC_APPOINTMENT_DONE]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: ETreatmentPlanDialogs.SCHEDULE_TC_APPOINTMENT_DONE,
        },
      },
      [ETreatmentPlanJobSystemSignal.ORDER_ALIGNERS_ARRIVED]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: ETreatmentPlanDialogs.ORDER_ALIGNERS_ARRIVED,
        },
      },
      [ETreatmentPlanJobSystemSignal.ORDER_ALIGNERS_APPLIED]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: ETreatmentPlanDialogs.ORDER_ALIGNERS_APPLIED,
        },
      },
      [ETreatmentPlanJobSystemSignal.PATIENT_RECORDS_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: ETreatmentPlanDialogs.PATIENT_RECORDS_CREATED,
        },
      },
      [ETreatmentPlanJobSystemSignal.REFERRAL_PAID_PROPOSAL_PRICE_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: ETreatmentPlanDialogs.REFERRAL_PRICE_PAID_PROPOSAL_CREATED,
        },
      },
      [ETreatmentPlanJobSystemSignal.REFERRAL_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          id: ETreatmentPlanDialogs.REFERRAL_CREATED,
        },
      },
      [ETreatmentPlanJobSystemSignal.REFERRAL_APPOINTMENT_DONE]: {
        [ETreatmentPlanJobState.PENDING]: {
          action: async (): Promise<void> => {
            await actionReferralAppointment(
              {
                id: "c166b11d-d83d-4033-8a3b-21bc7819bf94", // TODO: get from patient
                action: TReferralAppointmentActions.DONE,
              },
              {
                onSuccess: () =>
                  snackStore.showSuccess(
                    t("generic.messages.success.done", {
                      entity: t("generic.entity.referralAppointment"),
                    })
                  ),
              }
            );
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.REFERRAL_ACCEPTED]: {
        [ETreatmentPlanJobState.PENDING]: {
          action: async (): Promise<void> => {
            if (!currentReferral.value?.id) {
              throw new Error("No pending referral found.");
            }

            await acceptReferral(
              { id: currentReferral.value.id },
              {
                onSuccess: () =>
                  snackStore.showSuccess(
                    t("generic.messages.success.accept", {
                      entity: t("generic.entity.referral"),
                    })
                  ),
              }
            );
          },
          ...actionGate(
            {
              blocked: !can($permActions.ACCEPT_REFERRAL, $permSubjects.REFERRAL__REFERRAL),
              tooltip: t("generic.errors.permission_denied"),
            },
            {
              blocked: !currentReferral.value?.id,
              tooltip: t("generic.errors.notFoundEntity", {
                entity: t("generic.entity.referral"),
              }),
            }
          ),
          needsConfirmation: true,
        },
      },

      [ETreatmentPlanJobSystemSignal.REFERRAL_REJECTED]: {
        [ETreatmentPlanJobState.PENDING]: {
          action: async (): Promise<void> => {
            if (!currentReferral.value?.id) {
              throw new Error("No pending referral found.");
            }

            await rejectReferral(
              { id: currentReferral.value.id },
              {
                onSuccess: async () => {
                  await router.push({ name: "/dashboard/all-patients" });

                  snackStore.showSuccess(
                    t("generic.messages.success.reject", {
                      entity: t("generic.entity.referral"),
                    })
                  );
                },
              }
            );
          },
          noRefetchOnComplete: true,
          ...actionGate(
            {
              blocked: !can($permActions.REJECT_REFERRAL, $permSubjects.REFERRAL__REFERRAL),
              tooltip: t("generic.errors.permission_denied"),
            },
            {
              blocked: !currentReferral.value?.id,
              tooltip: t("generic.errors.notFoundEntity", {
                entity: t("generic.entity.referral"),
              }),
            }
          ),
          needsConfirmation: true,
          destructive: true,
          icon: "mdi-account-cancel-outline",
        },
      },

      [ETreatmentPlanJobSystemSignal.ORTHO_PROPOSED_PLAN_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/ortProposedPlan/add`,
            query: {
              patient: context.value.patient.id,
            },
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.ORTHO_PROPOSED_PLAN_CONFIRMED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/ortProposedPlan/${context.value.orthoProposedPlan ? context.value.orthoProposedPlan.id : ""}/edit`,
          },
          action: async (): Promise<void> => {
            await markOrthoProposedPlan(
              {
                type: TOrthoProposedPlanMarkType.CONFIRM,
                id: context.value.orthoProposedPlan ? context.value.orthoProposedPlan.id : "",
              },
              {
                onSuccess: () => {
                  snackStore.showSuccess(
                    t("generic.messages.success.confirm", {
                      entity: t("generic.entity.orthoProposedPlan"),
                    })
                  );
                  router.push(`/rooms/patient/${context.value.patient.id}`);
                },
              }
            );
          },
          disabled: !context.value.orthoProposedPlan,
          needsConfirmation: true,
        },
      },
      [ETreatmentPlanJobSystemSignal.PATIENT_FINANCING_PLAN_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          action: async (): Promise<void> => {
            await financingPlan(
              {
                patient: context.value.patient.id,
                financing_type: TPatientFinancingPlanType.ORTHO_FI,
              },
              { onSuccess: () => window.open(ORTHO_FI_URL, "_blank") }
            );
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.ORDER_ALIGNERS_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/patients/${context.value.patient.id}/order-aligners/add`,
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.ALIGNER_DELIVERY_INSTRUCTIONS_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/patients/${context.value.patient.id}/aligner-delivery-instructions/add`,
            query: {
              patient: context.value.patient.id,
            },
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.ALIGNER_DELIVERY_INSTRUCTIONS_CONFIRMED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/aligner-delivery/${context.value.alignerDeliveryInstructions ? context.value.alignerDeliveryInstructions.id : ""}/edit`,
          },
          disabled: !context.value.alignerDeliveryInstructions,
        },
      },
      [ETreatmentPlanJobSystemSignal.ALIGNER_DELIVERY_INSTRUCTIONS_SENT_RESULT]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/aligner-delivery/${context.value.alignerDeliveryInstructions ? context.value.alignerDeliveryInstructions.id : ""}/results`,
          },
          disabled: !context.value.alignerDeliveryInstructions,
        },
      },
      [ETreatmentPlanJobSystemSignal.ALIGNER_DELIVERY_INSTRUCTIONS_COMPLETED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/aligner-delivery/${context.value.alignerDeliveryInstructions ? context.value.alignerDeliveryInstructions.id : ""}/results`,
          },
          disabled: !context.value.alignerDeliveryInstructions,
        },
      },
      [ETreatmentPlanJobSystemSignal.NEXT_VISIT_INSTRUCTIONS_V2_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/patients/${context.value.patient.id}/next-visit-instructions-v2/add`,
            query: {
              patient: context.value.patient.id,
            },
          },
        },
        [ETreatmentPlanJobState.COMPLETED]: {
          to: {
            path: `/patients/${context.value.patient.id}/next-visit-instructions-v2/add`,
            query: {
              patient: context.value.patient.id,
            },
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.NEXT_VISIT_INSTRUCTIONS_V2_CONFIRMED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/next/${context.value.nextVisitInstructionsV2 ? context.value.nextVisitInstructionsV2.id : ""}/edit`,
          },
          disabled: !context.value.nextVisitInstructionsV2,
        },
      },
      [ETreatmentPlanJobSystemSignal.NEXT_VISIT_INSTRUCTIONS_V2_SENT_RESULT]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/next/${context.value.nextVisitInstructionsV2 ? context.value.nextVisitInstructionsV2.id : ""}/results`,
          },
          disabled: !context.value.nextVisitInstructionsV2,
        },
      },
      [ETreatmentPlanJobSystemSignal.NEXT_VISIT_INSTRUCTIONS_V2_COMPLETED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/next/${context.value.nextVisitInstructionsV2 ? context.value.nextVisitInstructionsV2.id : ""}/results`,
          },
          disabled: !context.value.nextVisitInstructionsV2,
        },
      },
      [ETreatmentPlanJobSystemSignal.FINAL_DEBOND_VISIT_INSTRUCTIONS_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/patients/${context.value.patient.id}/final-debond-instructions/add`,
            query: {
              patient: context.value.patient.id,
            },
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.FINAL_DEBOND_VISIT_INSTRUCTIONS_CONFIRMED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/final-debond/${context.value.finalDebondInstructions ? context.value.finalDebondInstructions.id : ""}/edit`,
          },
          disabled: !context.value.finalDebondInstructions,
        },
      },
      [ETreatmentPlanJobSystemSignal.FINAL_DEBOND_VISIT_INSTRUCTIONS_SENT_RESULT]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/final-debond/${context.value.finalDebondInstructions ? context.value.finalDebondInstructions.id : ""}/results`,
          },
          disabled: !context.value.finalDebondInstructions,
        },
      },
      [ETreatmentPlanJobSystemSignal.FINAL_DEBOND_VISIT_INSTRUCTIONS_COMPLETED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/visit-instructions/final-debond/${context.value.finalDebondInstructions ? context.value.finalDebondInstructions.id : ""}/results`,
          },
          disabled: !context.value.finalDebondInstructions,
        },
      },
      [ETreatmentPlanJobSystemSignal.INSTRUCTIONS_FOR_NEXT_VISIT_CREATED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/patients/${context.value.patient.id}/next-visit-instructions/add`,
            query: {
              patient: context.value.patient.id,
            },
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.INSTRUCTIONS_FOR_NEXT_VISIT_CONFIRMED]: {
        [ETreatmentPlanJobState.PENDING]: {
          to: {
            path: `/next-visit-instructions/${context.value.nextVisitInstructions ? context.value.nextVisitInstructions.id : ""}/edit`,
          },
          action: async (): Promise<void> => {
            await markNextVisitInstructions(
              {
                type: TNextVisitMarkType.CONFIRM,
                id: context.value.nextVisitInstructions
                  ? context.value.nextVisitInstructions.id
                  : "",
              },
              {
                onSuccess: () => {
                  snackStore.showSuccess(
                    t("generic.messages.success.confirm", {
                      entity: t("generic.entity.nextVisitInstruction"),
                    })
                  );
                  router.push(`/rooms/patient/${context.value.patient.id}`);
                },
              }
            );
          },
          disabled: !context.value.nextVisitInstructions,
          needsConfirmation: true,
        },
      },
      [ETreatmentPlanJobSystemSignal.INSTRUCTIONS_FOR_NEXT_VISIT_COMPLETED]: {
        [ETreatmentPlanJobState.PENDING]: {
          action: async (): Promise<void> => {
            await markNextVisitInstructions(
              {
                type: TNextVisitMarkType.COMPLETED,
                id: context.value.nextVisitInstructions
                  ? context.value.nextVisitInstructions.id
                  : "",
              },
              {
                onSuccess: () =>
                  snackStore.showSuccess(
                    t("generic.messages.success.complete", {
                      entity: t("generic.entity.nextVisitInstruction"),
                    })
                  ),
              }
            );
          },
        },
      },
      [ETreatmentPlanJobSystemSignal.SEND_DM_LOGIN_LINK]: {
        [ETreatmentPlanJobState.PENDING]: {
          action: async (): Promise<void> => {
            await sendDmLoginLink({ patientId: context.value.patient.id, jobId: job.id });
            snackStore.showSuccess(
              t("generic.messages.success.complete", {
                entity: job.name,
              })
            );
          },
          needsConfirmation: true,
        },
      },
      [ETreatmentPlanJobSystemSignal.ARCHIVE]: {
        [ETreatmentPlanJobState.PENDING]: {
          action: async (): Promise<void> => {
            const treatmentPlanId = getActualTreatmentPlanId(context.value.patient);
            if (!treatmentPlanId) {
              throw new Error("No active treatment plan found.");
            }

            await archiveTreatmentPlan(
              { treatmentPlanId, jobId: job.id },
              {
                onSuccess: async () => {
                  await router.push({
                    name: "/dashboard/all-patients",
                    query: { tab: EPatientDashboardTab.ARCHIVED },
                  });

                  snackStore.showSuccess(
                    t("generic.messages.success.complete", {
                      entity: job.name,
                    })
                  );

                  refetchPatientCard(context.value.patient.id);
                },
              }
            );
          },
          noRefetchOnComplete: true,
          needsConfirmation: true,
          destructive: true,
          icon: "mdi-archive-outline",
        },
      },
    };
  }

  const availableJobs = computed(() =>
    jobs.value
      ? jobs.value.map((rawJob) => {
          const job = normalizeJobState(rawJob);
          let action!: TTreatmentPlanJobMeta;
          let dialog: {
            title?: string;
            subtitle?: string;
            cancelText?: string;
            acceptText?: string;
          } = {};
          try {
            action = {
              text: resolveText(job),
            };

            dialog = resolveDialog(job);

            const signalType =
              job.signal_system_type || job.signal_manual_type || job.signal_automatic_type;
            const systemJobs = systemJobsMap(job);
            let definedJobMeta = undefined;
            if (signalType) {
              const signalJobs = systemJobs[signalType];
              if (signalJobs && signalJobs[job.state]) {
                definedJobMeta = signalJobs[job.state];
              }
            }

            if (job.signal_type === ETreatmentPlanJobType.SYSTEM || job.signal_type === null) {
              // Add job_id to 'to' route if it exists
              const jobMetaWithJobId = definedJobMeta?.to
                ? {
                    ...definedJobMeta,
                    to: addJobIdToRoute(definedJobMeta.to as any, job.id, route),
                  }
                : definedJobMeta;

              action = {
                ...action,
                ...jobMetaWithJobId,
              };
            } else if (job.signal_type === ETreatmentPlanJobType.MANUAL) {
              // Add job_id to 'to' route if it exists
              const jobMetaWithJobId = definedJobMeta?.to
                ? {
                    ...definedJobMeta,
                    to: addJobIdToRoute(definedJobMeta.to as any, job.id, route),
                  }
                : definedJobMeta;

              action = {
                ...action,
                needsConfirmation: true,
                action: async (): Promise<void> => {
                  await TreatmentPlanJobService.changeManualJobState(job);
                  snackStore.showSuccess(
                    t("generic.messages.success.complete", {
                      entity: job.name,
                    })
                  );
                  if (action.innerAction) {
                    await action.innerAction();
                  }
                },
                ...jobMetaWithJobId,
              };
            }

            if (action.action) {
              const originalOnClick = action.action;
              action.onClickEvent = async (): Promise<void> => {
                context.value.loading.value = true;
                try {
                  await originalOnClick?.();
                } catch (error) {
                  logError(error, {
                    context: {
                      feature: "treatment-plan-job-actions",
                      step: "onClickEvent",
                      jobId: job.id ?? null,
                    },
                  });
                  snackStore.showError(t("generic.errors.unknownError"));
                } finally {
                  if (!action.noRefetchOnComplete)
                    await refetchPatientCard(context.value.patient.id);
                  await updateUrlWithJobId(null, router, route);
                  context.value.loading.value = false;
                }
              };
            }

            return {
              ...job,
              dialog,
              _: action,
            };
          } catch (error) {
            logError(error, {
              context: {
                feature: "treatment-plan-job-actions",
                step: "availableJobs",
                jobId: job.id ?? null,
              },
            });
            if (snackStore) {
              snackStore.showError(error);
            }
            return {
              ...job,
              dialog,
              _: action,
            };
          }
        })
      : []
  );

  watch(
    availableJobs,
    (list) => {
      treatmentPlanStore.registerJobs(jobSourceKey, list ?? []);
    },
    { immediate: true }
  );

  return availableJobs;
};
